import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TwoFactorDialog } from "@/components/TwoFactorDialog";
import { toast } from "sonner";
import { CheckCircle2, Wallet } from "lucide-react";

export default function Redeem() {
  const [params] = useSearchParams();
  const [requestedHolding] = useState(() => params.get("holding") || "");
  const [requestedScheme] = useState(() => params.get("scheme") || "");
  const navigate = useNavigate();
  const [holdings, setHoldings] = useState([]);
  const [holdingKey, setHoldingKey] = useState(requestedHolding);
  const [schemeCode, setSchemeCode] = useState(requestedScheme);
  const [mode, setMode] = useState("amount");
  const [amount, setAmount] = useState("");
  const [qty, setQty] = useState("");
  const [allRedeem, setAllRedeem] = useState(false);
  const [accountNo, setAccountNo] = useState("");
  const [folio, setFolio] = useState("");
  const [demat, setDemat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [twoFa, setTwoFa] = useState({ open: false, order: null });

  const applyHolding = (row) => {
    if (!row) return;
    setHoldingKey(row.holding_key || row.scheme_code);
    setSchemeCode(row.scheme_code);
    setFolio(row.folio_no || "");
    setDemat(row.holding_mode === "DEMAT");
  };

  useEffect(() => {
    clientApi.get("/portfolio/holdings").then((r) => {
      const rows = (r.data.holdings || []).filter((h) => Number(h.units) > 0);
      setHoldings(rows);
      const selected = rows.find((row) => row.holding_key === requestedHolding) ||
        rows.find((row) => row.scheme_code === requestedScheme) || rows[0];
      applyHolding(selected);
    }).catch((err) => toast.error(getApiErrorMessage(err)));
  }, [requestedHolding, requestedScheme]);

  const handleRedeem = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await clientApi.post("/orders/redemption", {
        scheme_code: schemeCode,
        amount: mode === "amount" && !allRedeem ? Number(amount) : undefined,
        qty: mode === "qty" && !allRedeem ? Number(qty) : undefined,
        all_redeem: allRedeem,
        bank_account_no: accountNo,
        folio_no: folio,
        demat,
      });
      if (data.requires_bse_auth || data.requires_2fa) {
        setTwoFa({ open: true, order: data });
      } else {
        setResult(data);
        if (data.bse2?.status === "SUBMITTED") toast.success(data.bse2?.message || "Redemption accepted by BSE.");
        else toast.warning(data.bse2?.message || "BSE confirmation is pending; do not submit again.");
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    await clientApi.post(`/orders/${twoFa.order.order_number}/bse-auth/complete`);
    setTwoFa({ open: false, order: null });
    setResult(twoFa.order);
    toast.success("BSE authentication verified.");
  };

  if (result) {
    return (
      <ClientLayout>
        <div className="mx-auto max-w-lg py-10 text-center">
          <CheckCircle2 className="mx-auto h-16 w-16 text-[var(--cta)]" />
          <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">Redemption submitted</h1>
          <p className="mt-2 text-sm text-[var(--ink-muted)]">Order #{result.order_number}</p>
          <Button className="mt-6 rounded-full bg-[var(--cta)]" onClick={() => navigate("/orders")} data-testid="redeem-result-view-orders-button">View orders</Button>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <Wallet className="h-6 w-6 text-[var(--cta)]" />
          <h1 className="font-heading text-2xl text-[var(--brand)]">Redeem</h1>
        </div>
        <Card className="glass-card border-0">
          <CardHeader><CardTitle className="font-heading text-lg text-[var(--brand)]">Redemption request</CardTitle></CardHeader>
          <CardContent>
            {holdings.length === 0 ? (
              <div className="text-sm text-[var(--ink-muted)]" data-testid="redeem-no-holdings-message">No allotted units are currently available for redemption. Refresh your BSE portfolio from the Orders page if an allotment is missing.</div>
            ) : (
              <form className="space-y-5" onSubmit={handleRedeem}>
                <div className="space-y-1.5">
                  <Label>Holding to redeem</Label>
                  <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={holdingKey} onChange={(e) => applyHolding(holdings.find((h) => (h.holding_key || h.scheme_code) === e.target.value))} data-testid="redeem-scheme-select">
                    {holdings.map((h) => <option key={h.holding_key || h.scheme_code} value={h.holding_key || h.scheme_code}>{h.scheme_name} · folio {h.folio_no || "—"} · {h.holding_mode || "PHYSICAL"} · {h.units} units</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3 rounded-xl bg-[var(--surface-2)] p-3 text-xs"><div><span className="text-[var(--ink-muted)]">Folio</span><div className="font-medium">{folio || "—"}</div></div><div><span className="text-[var(--ink-muted)]">Holding mode</span><div className="font-medium">{demat ? "DEMAT" : "PHYSICAL"}</div></div></div>
                <div className="flex items-center gap-2">
                  <Checkbox id="all-redeem" checked={allRedeem} onCheckedChange={setAllRedeem} data-testid="redeem-all-checkbox" />
                  <Label htmlFor="all-redeem">Redeem all units</Label>
                </div>
                {!allRedeem && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Amount (INR)</Label>
                      <Input type="number" value={amount} onChange={(e) => { setAmount(e.target.value); setMode("amount"); }} data-testid="redeem-amount-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>OR Units</Label>
                      <Input type="number" value={qty} onChange={(e) => { setQty(e.target.value); setMode("qty"); }} data-testid="redeem-qty-input" />
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Bank account for payout</Label>
                  <Input required value={accountNo} onChange={(e) => setAccountNo(e.target.value)} data-testid="redeem-account-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="redeem-submit-button">
                  {loading ? "Submitting..." : "Submit redemption"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      <TwoFactorDialog
        open={twoFa.open}
        title="Confirm your redemption (Redemption 2FA)"
        verificationLink={twoFa.order?.verification_link}
        onVerify={handleVerify2FA}
        onClose={() => setTwoFa({ open: false, order: null })}
      />
    </ClientLayout>
  );
}
